import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SyncOutlined } from '@ant-design/icons'
import Tooltip from 'antd/es/tooltip/index'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button'
import { useThrottledAction } from '@/common/hooks/use-throttled-action'
import './reload-control.scss'

interface ReloadControlProps {
  onReload: () => void
  variant?: 'icon' | 'button'
  /** Minimum gap between reloads. Omit or 0 for no throttling. */
  throttleMs?: number
  disabled?: boolean
}

export const ReloadControl = ({
  onReload,
  variant = 'icon',
  throttleMs = 0,
  disabled = false,
}: ReloadControlProps) => {
  const { t } = useTranslation()
  const [isSpinning, setIsSpinning] = useState(false)
  const [throttleRunId, setThrottleRunId] = useState(0)

  // Read onReload through a ref so an unstable callback (inline arrow, RTK Query
  // refetch) cannot rebuild the throttle and defeat it.
  const onReloadRef = useRef(onReload)
  useEffect(() => {
    onReloadRef.current = onReload
  }, [onReload])

  const stableReload = useCallback(() => onReloadRef.current(), [])

  const { trigger, isThrottled } = useThrottledAction(
    stableReload,
    throttleMs || undefined
  )

  const isThrottleEnabled = throttleMs > 0
  const isDisabled = disabled || (isThrottleEnabled && isThrottled)

  const handleTrigger = useCallback(() => {
    if (isDisabled) return

    setIsSpinning(true)
    if (isThrottleEnabled) {
      setThrottleRunId((prev) => prev + 1)
      trigger()
    } else {
      stableReload()
    }
  }, [isDisabled, isThrottleEnabled, trigger, stableReload])

  const tooltipTitle =
    isThrottleEnabled && isThrottled
      ? t('common.reload_button.cooling_down')
      : t('common.reload_button.reload')

  const icon = (
    <SyncOutlined
      className={isSpinning ? 'reload-control__spin' : undefined}
      onAnimationEnd={() => setIsSpinning(false)}
    />
  )

  if (variant === 'icon') {
    return (
      <Tooltip title={tooltipTitle}>
        <span
          className={`reload-control__icon${isDisabled ? ' reload-control__icon--disabled' : ''}`}
          onClick={handleTrigger}
        >
          {icon}
        </span>
      </Tooltip>
    )
  }

  // The mask is a sibling of the button, not a child: antd adds icon margin when
  // a Button has children, and a disabled Button swallows the Tooltip's events.
  return (
    <Tooltip title={tooltipTitle}>
      <span className={'reload-control__button'}>
        <IdeyaButton
          ideyaType={'antd'}
          icon={icon}
          onClick={handleTrigger}
          disabled={isDisabled}
        />
        {isThrottleEnabled && throttleRunId > 0 && (
          <span
            key={throttleRunId}
            className={'reload-control__cooldown-mask'}
            style={
              {
                '--reload-cooldown-duration': `${throttleMs}ms`,
              } as React.CSSProperties
            }
          />
        )}
      </span>
    </Tooltip>
  )
}
